if (!customElements.get('sth-size-check')) {
  customElements.define('sth-size-check', class extends HTMLElement {
    connectedCallback() {
      if (this.sthInitialized) return;
      this.sthInitialized = true;
      this.dialog = this.querySelector('.size-check__dialog');
      this.form = this.querySelector('form');
      this.result = this.querySelector('.size-check__result');
      this.config = JSON.parse(this.querySelector('[data-size-check-config]').textContent);
      this.trigger = this.querySelector('.size-check__trigger');
      this.sizeChart = this.parseSizeChart();
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeDialog();
      });
      document.body.append(this.dialog);
      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openDialog();
      });
      ['click', 'input', 'change', 'submit'].forEach((type) => {
        this.dialog.addEventListener(type, (event) => event.stopPropagation());
      });
      this.dialog.querySelector('.size-check__close').addEventListener('click', () => this.closeDialog());
      this.form.elements.units.forEach((input) => input.addEventListener('change', () => this.changeUnits(input.value)));
      this.form.addEventListener('submit', (event) => this.recommend(event));
      this.dialog.querySelector('.size-check__restart').addEventListener('click', () => this.restart());
    }

    openDialog() {
      this.scrollPosition = window.scrollY;
      this.bodyStyles = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width
      };
      this.syncVisualViewport = () => {
        const viewport = window.visualViewport;
        const viewportHeight = viewport?.height || window.innerHeight;
        this.dialog.style.setProperty('--size-check-viewport-height', `${viewportHeight}px`);
        window.requestAnimationFrame(() => {
          const focusedControl = this.dialog.querySelector('input:focus, select:focus');
          focusedControl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      };
      this.syncVisualViewport();
      window.visualViewport?.addEventListener('resize', this.syncVisualViewport);
      window.visualViewport?.addEventListener('scroll', this.syncVisualViewport);
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      this.dialog.hidden = false;
      this.dialog.querySelector('.size-check__panel').scrollTop = 0;
      this.dialog.querySelector('.size-check__close').focus();
    }

    closeDialog() {
      this.dialog.hidden = true;
      window.visualViewport?.removeEventListener('resize', this.syncVisualViewport);
      window.visualViewport?.removeEventListener('scroll', this.syncVisualViewport);
      this.dialog.style.removeProperty('--size-check-viewport-height');
      this.dialog.style.removeProperty('--size-check-viewport-width');
      this.dialog.style.removeProperty('--size-check-viewport-top');
      this.dialog.style.removeProperty('--size-check-viewport-left');
      document.body.style.overflow = this.bodyStyles?.overflow || '';
      document.body.style.position = this.bodyStyles?.position || '';
      document.body.style.top = this.bodyStyles?.top || '';
      document.body.style.width = this.bodyStyles?.width || '';
      window.scrollTo(0, this.scrollPosition || 0);
      this.trigger.focus();
    }

    changeUnits(units) {
      const metric = units === 'metric';
      this.dialog.querySelector('.size-check__metric-inputs').hidden = !metric;
      this.dialog.querySelector('.size-check__imperial-inputs').hidden = metric;
      this.form.elements.heightCm.required = metric;
      this.form.elements.weightKg.required = metric;
      this.form.elements.heightFt.required = !metric;
      this.form.elements.weightLb.required = !metric;
      this.changeBodyUnits(metric ? 'cm' : 'in');
    }

    changeBodyUnits(units) {
      this.dialog.querySelectorAll('[data-body-unit]').forEach((unit) => {
        unit.textContent = units === 'cm' ? 'cm' : 'in';
      });
      this.dialog.querySelectorAll('[data-body-measurement]').forEach((input) => {
        input.placeholder = units === 'cm' ? input.dataset.cmPlaceholder : input.dataset.inPlaceholder;
      });
    }

    normalizeSize(value) {
      return String(value).trim().toUpperCase()
        .replace('EXTRA SMALL', 'XS')
        .replace('SMALL', 'S')
        .replace('MEDIUM', 'M')
        .replace('LARGE', 'L');
    }

    normalizeLabel(value) {
      const label = String(value).toLowerCase().replace(/[^a-z]/g, '');
      if (label.includes('chest') || label.includes('bust')) return 'chest';
      if (label.includes('waist')) return 'waist';
      if (label.includes('hip')) return 'hip';
      return label;
    }

    numberFromCell(value) {
      const match = String(value).replace(',', '.').match(/\d+(?:\.\d+)?/);
      return match ? Number(match[0]) : null;
    }

    parseSizeChart() {
      const root = this.querySelector('.size-check__measurements');
      if (!root) return {};
      const chart = {};
      root.querySelectorAll('table').forEach((table) => {
        const rows = [...table.querySelectorAll('tr')].map((row) =>
          [...row.querySelectorAll('th, td')].map((cell) => cell.textContent.trim())
        ).filter((row) => row.length > 1);
        if (rows.length < 2) return;
        const header = rows[0];
        const sizesAcross = header.slice(1).some((cell) =>
          this.config.sizes.some((size) => this.normalizeSize(size) === this.normalizeSize(cell))
        );
        if (sizesAcross) {
          rows.slice(1).forEach((row) => {
            const field = this.normalizeLabel(row[0]);
            header.slice(1).forEach((size, index) => {
              const normalized = this.normalizeSize(size);
              if (!chart[normalized]) chart[normalized] = {};
              const value = this.numberFromCell(row[index + 1]);
              if (value !== null) chart[normalized][field] = value;
            });
          });
        } else {
          const labels = header.map((cell) => this.normalizeLabel(cell));
          rows.slice(1).forEach((row) => {
            const normalized = this.normalizeSize(row[0]);
            if (!this.config.sizes.some((size) => this.normalizeSize(size) === normalized)) return;
            if (!chart[normalized]) chart[normalized] = {};
            labels.slice(1).forEach((field, index) => {
              const value = this.numberFromCell(row[index + 1]);
              if (value !== null) chart[normalized][field] = value;
            });
          });
        }
      });
      if (Object.keys(chart).length === 0) {
        let currentSize = null;
        root.textContent.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).forEach((line) => {
          const matchedSize = this.config.sizes.find((size) =>
            this.normalizeSize(size) === this.normalizeSize(line)
          );
          if (matchedSize) {
            currentSize = this.normalizeSize(matchedSize);
            if (!chart[currentSize]) chart[currentSize] = {};
            return;
          }
          if (!currentSize || !line.includes(':')) return;
          const separator = line.indexOf(':');
          const field = this.normalizeLabel(line.slice(0, separator));
          const value = this.numberFromCell(line.slice(separator + 1));
          if (value !== null) chart[currentSize][field] = value;
        });
      }
      return chart;
    }

    fitProfile() {
      const text = `${this.config.productTitle} ${this.config.description}`.toLowerCase();
      if (text.includes('oversized')) return 'oversized';
      if (text.includes('close-fitting') || text.includes('compact fit') || text.includes('held close') || text.includes('slim fit')) return 'slim';
      if (text.includes('boxy')) return 'boxy';
      if (text.includes('relaxed') || text.includes('wide-leg') || text.includes('wide leg')) return 'relaxed';
      return 'regular';
    }

    hasFlexibleWaist() {
      const text = `${this.config.productTitle} ${this.config.description}`.toLowerCase();
      return /elastic(?:ated)?(?:-| )?(?:back(?:ed)?(?:-| )?)?waist(?:band)?|stretch(?:y)?(?:-| )?waist(?:band)?|drawstring(?:-| )?waist|adjustable(?:-| )?waist/.test(text);
    }

    sizeIndex(value, sizes) {
      const normalized = this.normalizeSize(value);
      return sizes.findIndex((size) => this.normalizeSize(size) === normalized);
    }

    bodyIndex(height, weight, count) {
      const bmi = weight / Math.pow(height / 100, 2);
      const score = bmi + (height - 175) * 0.075;
      const thresholds = count >= 4 ? [20.5, 23.5, 27] : [22, 26];
      return Math.min(thresholds.filter((threshold) => score >= threshold).length, count - 1);
    }

    bodyAtUpperBoundary(height, weight, count) {
      const bmi = weight / Math.pow(height / 100, 2);
      const score = bmi + (height - 175) * 0.075;
      const thresholds = count >= 4 ? [20.5, 23.5, 27] : [22, 26];
      const index = Math.min(thresholds.filter((threshold) => score >= threshold).length, count - 1);
      const nextThreshold = thresholds[index];
      return nextThreshold !== undefined && nextThreshold - score <= 0.65;
    }

    bodyMeasurement(data, name) {
      const raw = Number(data.get(name));
      if (!raw) return null;
      return data.get('units') === 'imperial' ? raw * 2.54 : raw;
    }

    garmentCircumference(field, value) {
      if (value === null || value === undefined) return null;
      if (field !== 'chest' && field !== 'waist' && field !== 'hip') return value;
      const chartValues = Object.values(this.sizeChart)
        .map((row) => row[field])
        .filter((measurement) => Number.isFinite(measurement));
      const chartUsesHalfWidth = chartValues.length > 0 && chartValues.every((measurement) => measurement < 75);
      return chartUsesHalfWidth ? value * 2 : value;
    }

    targetEase(field, fit) {
      const ease = {
        chest: { slim: 6, regular: 10, relaxed: 16, boxy: 10, oversized: 24 },
        waist: { slim: 2, regular: 5, relaxed: 8, boxy: 5, oversized: 10 },
        hip: { slim: 4, regular: 8, relaxed: 13, boxy: 8, oversized: 18 }
      };
      return ease[field]?.[fit] ?? 8;
    }

    measurementScore(size, measurements, fit) {
      const row = this.sizeChart[this.normalizeSize(size)] || {};
      let total = 0;
      let used = 0;
      Object.entries(measurements).forEach(([field, bodyValue]) => {
        if (!bodyValue || row[field] === undefined) return;
        const garment = this.garmentCircumference(field, row[field]);
        const desired = bodyValue + this.targetEase(field, fit);
        const difference = garment - desired;
        const penalty = difference < -1 ? Math.abs(difference) * 1.8 : Math.abs(difference);
        total += Math.max(0, 100 - penalty * 8);
        used += 1;
      });
      return used ? total / used : null;
    }

    recommend(event) {
      event.preventDefault();
      const error = this.dialog.querySelector('.size-check__error');
      if (!this.form.reportValidity()) {
        error.hidden = false;
        return;
      }
      error.hidden = true;
      const data = new FormData(this.form);
      const units = data.get('units');
      const height = units === 'metric'
        ? Number(data.get('heightCm'))
        : (Number(data.get('heightFt')) * 12 + Number(data.get('heightIn') || 0)) * 2.54;
      const weight = units === 'metric' ? Number(data.get('weightKg')) : Number(data.get('weightLb')) * 0.453592;
      const sizes = this.config.sizes;
      const fit = this.fitProfile();
      const baseIndex = this.bodyIndex(height, weight, sizes.length);
      const usualIndex = this.sizeIndex(data.get('usualSize'), sizes);
      const measurements = {
        chest: this.bodyMeasurement(data, 'chest'),
        waist: this.bodyMeasurement(data, 'waist'),
        hip: this.bodyMeasurement(data, 'hip')
      };
      const hasMeasurement = Object.values(measurements).some(Boolean);
      const rawWaists = sizes.map((size) =>
        this.sizeChart[this.normalizeSize(size)]?.waist
      ).filter((value) => Number.isFinite(value));
      const availableWaists = rawWaists.length && rawWaists.every((value) => value < 75)
        ? rawWaists.map((value) => value * 2)
        : rawWaists;
      const maxAvailableWaist = availableWaists.length ? Math.max(...availableWaists) : null;
      if (
        measurements.waist
        && maxAvailableWaist !== null
        && measurements.waist > maxAvailableWaist
        && !this.hasFlexibleWaist()
      ) {
        this.showNoMatch();
        return;
      }
      const preferredFit = data.get('preferredFit');
      const atUpperBoundary = this.bodyAtUpperBoundary(height, weight, sizes.length);
      const buildShift = data.get('build') === 'broad' ? 0.45 : data.get('build') === 'slim' ? -0.2 : 0;
      let preferenceShift = preferredFit === 'close' ? -0.2 : 0;
      if (preferredFit === 'relaxed') {
        if (fit === 'slim') preferenceShift = 0.55;
        if (fit === 'oversized' && atUpperBoundary) preferenceShift = 0.35;
      }
      const fitShift = fit === 'slim' ? 0.35 : 0;
      const adjustedBodyIndex = baseIndex + buildShift + preferenceShift + fitShift;

      const ranked = sizes.map((size, index) => {
        const bodyFitScore = Math.max(0, 100 - Math.abs(index - adjustedBodyIndex) * 38);
        const garmentScore = this.measurementScore(size, measurements, fit);
        const usualScore = usualIndex >= 0 ? Math.max(0, 100 - Math.abs(index - usualIndex) * 50) : 50;
        const total = garmentScore === null
          ? bodyFitScore * 0.92 + usualScore * 0.08
          : bodyFitScore * 0.5 + garmentScore * 0.45 + usualScore * 0.05;
        return { size, index, total, garmentScore };
      }).sort((a, b) => b.total - a.total);

      const hasStrongMeasurement = hasMeasurement && ranked.some((item) =>
        item.garmentScore !== null && item.garmentScore >= 75
      );
      const eligible = usualIndex >= 0 && !hasStrongMeasurement
        ? ranked.filter((item) => Math.abs(item.index - usualIndex) <= 1)
        : ranked;
      const first = eligible[0];
      const second = eligible[1];
      const closeResult = second && first.total - second.total <= 7;
      const measurementConflict = hasMeasurement && first.garmentScore !== null && first.garmentScore < 65;
      const canOfferRelaxedAlternative = preferredFit === 'relaxed'
        && first.index < sizes.length - 1
        && (
          fit === 'regular'
          || fit === 'relaxed'
          || fit === 'boxy'
          || (fit === 'oversized' && atUpperBoundary)
        );
      const relaxedAlternative = canOfferRelaxedAlternative
        ? ranked.find((item) => item.index === first.index + 1)
        : null;
      const isFitPreferenceChoice = Boolean(relaxedAlternative);
      let showTwo = Boolean(relaxedAlternative || (second && (closeResult || measurementConflict)));
      let recommendations = relaxedAlternative
        ? [first.size, relaxedAlternative.size]
        : showTwo
          ? [first, second].sort((a, b) => a.index - b.index).map((item) => item.size)
          : [first.size];

      const isColumnPleatTrousers = this.config.productTitle.trim().toLowerCase() === 'column pleat trousers';
      if (isColumnPleatTrousers) {
        recommendations = [sizes[Math.min(first.index + 1, sizes.length - 1)]];
        showTwo = false;
      }

      this.showResult(recommendations, fit, showTwo, {
        isFitPreferenceChoice: isFitPreferenceChoice && showTwo,
        atUpperBoundary
      });
    }

    showNoMatch() {
      const title = this.dialog.querySelector('.size-check__result-title');
      const copy = this.dialog.querySelector('.size-check__result-copy');
      const actions = this.dialog.querySelector('.size-check__result-actions');
      title.textContent = 'No suitable size';
      copy.textContent = 'Based on the waist measurement entered, none of the available sizes is likely to fit comfortably. We do not want to recommend the largest size when its garment measurement is smaller than yours.';
      actions.replaceChildren();
      this.form.hidden = true;
      this.result.hidden = false;
    }

    showResult(sizes, productFit, showTwo, context = {}) {
      const title = this.dialog.querySelector('.size-check__result-title');
      const copy = this.dialog.querySelector('.size-check__result-copy');
      const actions = this.dialog.querySelector('.size-check__result-actions');
      title.textContent = showTwo ? `${sizes[0]} or ${sizes[1]}` : `Size ${sizes[0]}`;
      if (context.isFitPreferenceChoice) {
        copy.textContent = productFit === 'oversized'
          ? `Size ${sizes[0]} is the best match. You are near the top of this size, so choose ${sizes[1]} only if you want extra room.`
          : `Size ${sizes[0]} is the best match. Choose ${sizes[1]} if you prefer a more relaxed fit.`;
      } else {
        copy.textContent = showTwo
          ? `You are close to two sizes. Choose ${sizes[0]} for a cleaner fit or ${sizes[1]} for more room.`
          : `Based on your details, measurements and this product's ${productFit} fit, ${sizes[0]} is the clearest match.`;
      }
      actions.replaceChildren();
      sizes.forEach((size, position) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = position === 0 ? 'button' : 'button button--secondary';
        button.textContent = `Select ${size}`;
        button.addEventListener('click', () => this.selectSize(size));
        actions.append(button);
      });
      this.form.hidden = true;
      this.result.hidden = false;
    }

    selectSize(size) {
      const fieldset = document.getElementById(`SizeSelector-${this.dataset.sectionId}`);
      const options = fieldset ? [...fieldset.querySelectorAll('input[type="radio"], option')] : [];
      const target = options.find((option) => this.normalizeSize(option.value) === this.normalizeSize(size));
      if (target) {
        if (target.tagName === 'OPTION') {
          target.parentElement.value = target.value;
          target.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          target.click();
        }
      }
      this.closeDialog();
    }

    restart() {
      this.result.hidden = true;
      this.form.hidden = false;
    }
  });
}
